import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchMeOrNull } from "@/lib/auth";
import { keys } from "@/lib/queries";
import { BALANCE_MODE_THIRD_PARTY_CREDIT, SESSION_ORIGIN_EXTERNAL_LAUNCH } from "@/lib/session";
import {
  LAUNCH_OUTCOME_QUERY,
  launchViewForClass,
  postHostLifecycle,
  type LaunchView,
} from "@/lib/externalCredit";
import { Button, Card, Spinner } from "@/components/ui";
import { BrandLogoMark, useBrandName } from "@/brand";
import { buildHead } from "@/lib/seo";

// SM-654 (SM-621 phase 15) — the provider launch landing.
//
// A provider opens https://<host>/launch/{broker_slug}?code=… top-level.
// That URL never reaches this SPA: nginx hands /launch/* to the gateway,
// which exchanges the one-time code server-side and answers 303 to THIS
// page with a clean URL — no query on success (the session cookie is
// set), `?launch=<class>` when it refused. So no application asset or
// script ever loads with the code in the address bar, and this page has
// nothing to persist, log or report: it never sees the code.
//
// What it does:
//	- reads the refusal class once and strips it from the address bar;
//	- on success, confirms the session exists (GET /api/auth/me) BEFORE
//	  any application data is initialised — it sits outside the _app
//	  layout, so no app query has run — then enters the app;
//	- otherwise renders a safe, actionable state: return to the provider
//	  app and launch again. There is deliberately no retry button — a
//	  code is single-use, and nothing here could re-send one anyway.
//
// It never mentions a balance moving: authentication changes neither
// system's balance (MINI-APP-FLOW "Secure Launch").
export const Route = createFileRoute("/auth/launch")({
  head: () => buildHead({ path: "/auth/launch", title: "Opening your account", noindex: true }),
  validateSearch: (s: Record<string, unknown>): LaunchSearch => ({
    [LAUNCH_OUTCOME_QUERY]: typeof s[LAUNCH_OUTCOME_QUERY] === "string" ? (s[LAUNCH_OUTCOME_QUERY] as string) : undefined,
  }),
  component: LaunchLandingPage,
});

interface LaunchSearch {
  launch?: string;
}

/** How long the success state shows before entering the app. */
const SUCCESS_HOLD_MS = 900;

function LaunchLandingPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // The refusal class is read ONCE: the address bar is cleaned right
  // after, and a refresh of the clean URL re-checks the session instead.
  const [view, setView] = useState<LaunchView>(() => launchViewForClass(search.launch) ?? "loading");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
    if (view !== "loading") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      const me = await fetchMeOrNull();
      if (cancelled) return;
      const launched =
        !!me && me.session_origin === SESSION_ORIGIN_EXTERNAL_LAUNCH && me.balance_mode === BALANCE_MODE_THIRD_PARTY_CREDIT;
      if (!launched) {
        // No session after a clean redirect: the cookie did not stick
        // (third-party cookie policy in the WebView, or a stale tab), or
        // this tab was opened by hand. Either way the safe answer is the
        // same — launch again from the provider app.
        setView("retry_safe_failure");
        return;
      }
      qc.setQueryData(keys.me(), me);
      setView("success");
      timer = setTimeout(() => navigate({ to: "/", replace: true }), SUCCESS_HOLD_MS);
    })();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on landing
  }, []);

  return <LaunchStateCard view={view} onEnter={() => navigate({ to: "/", replace: true })} />;
}

/**
 * The landing's states as one presentational card — also what the
 * mini-app preview route renders, so every state can be shot on SIT.
 */
export function LaunchStateCard({
  view,
  onEnter,
  fill = true,
}: {
  view: LaunchView;
  onEnter?: () => void;
  /** Fill the viewport (the landing); the preview lays several side by side. */
  fill?: boolean;
}) {
  const { t } = useTranslation("auth");
  const [hostTook, setHostTook] = useState(false);
  const failed = view !== "loading" && view !== "success";
  return (
    <div className={fill ? "flex min-h-dvh items-center justify-center bg-bg px-4 py-8" : "flex justify-center"}>
      <Card className="w-full max-w-sm p-6" data-launch-view={view}>
        <div className="space-y-4 text-center">
          <Brand />
          {view === "loading" ? (
            <div className="space-y-3 py-2" role="status" aria-live="polite">
              <Spinner className="mx-auto text-primary" size={24} />
              <p className="text-sm text-fg-muted">{t("launch.loading")}</p>
            </div>
          ) : view === "success" ? (
            <div className="space-y-3" role="status" aria-live="polite">
              <h1 className="text-lg font-semibold text-fg">{t("launch.successTitle")}</h1>
              <p className="text-sm text-fg-muted">{t("launch.successBody")}</p>
              <p className="text-xs text-fg-subtle">{t("launch.noBalanceMoved")}</p>
              {onEnter ? (
                <Button block onClick={onEnter}>
                  {t("launch.enter")}
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3" role="alert">
              <h1 className="text-lg font-semibold text-fg">{t(`launch.${view}.title`)}</h1>
              <p className="text-sm text-fg-muted">{t(`launch.${view}.body`)}</p>
              <p className="text-xs text-fg-subtle">{t("launch.noBalanceMoved")}</p>
              {failed ? (
                <Button
                  block
                  variant="secondary"
                  onClick={() => setHostTook(postHostLifecycle("REQUEST_CLOSE"))}
                >
                  {t("launch.returnToApp")}
                </Button>
              ) : null}
              {failed && !hostTook ? <p className="text-xs text-fg-subtle">{t("launch.closeHint")}</p> : null}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Brand() {
  const brand = useBrandName();
  return (
    <div className="flex items-center justify-center gap-1.5 text-fg">
      <BrandLogoMark className="h-5 w-5 text-primary" />
      <span className="font-semibold">{brand}</span>
    </div>
  );
}
